import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  Signal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SHARED_MODULES, Strategy } from '@app/shared';
import { debounceDelay, HttpParamsUtils, notNull, safeDate } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  addMonths,
  endOfMonth,
  endOfYear,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isToday,
  max,
  min,
  startOfMonth,
  startOfYear,
  subMonths,
  subYears,
} from 'date-fns';
import { first, flatten, head, isEmpty, isEqual, isNil, last, sortBy } from 'lodash';
import { combineLatest, distinctUntilChanged, map, Observable, startWith } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DataService, LoadData } from '../../services/data.service';
import { FilterService } from '../../services/filter.service';
import {
  Filter,
  FilterControlEnum,
  FilterForm,
  FILTERS,
  FilterTypeEnum,
  INTERVAL,
  SHORTCUTS,
  StatusFilter,
} from './filter-bar';

@Component({
  selector: 'app-posts-filter-bar',
  imports: [SHARED_MODULES],
  templateUrl: './posts-filter-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PostsFilterBarComponent implements AfterViewInit {
  public config = input<Filter>();
  public refresh = output<any>();
  protected store$: Observable<AppState>;
  protected form: FormGroup;
  protected userGroups$: Observable<any[]>;
  protected config$: Observable<any>;
  protected level: LoadData<any[]>;
  protected grade: LoadData<any[]>;
  protected group: LoadData<any[]>;
  protected subject: LoadData<any[]>;
  protected teacher: LoadData<any[]>;
  protected userGroups: LoadData<any[]>;
  protected readonly badgets: Signal<any[]>;
  protected readonly strategy = Strategy;
  protected readonly options = FILTERS;
  protected readonly shortcuts = SHORTCUTS;
  protected readonly currentOption = signal(FilterTypeEnum.Shortcut);
  protected readonly filterControl = FilterControlEnum;
  private readonly _store = inject(Store);
  private readonly _destroy = inject(DestroyRef);
  private readonly _dataService = inject(DataService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _filterService = inject(FilterService);
  private readonly _translateService = inject(TranslateService);

  constructor() {
    this.initForm();
    this.loadData();
    this.filterListeners();
    this.badgets = this.loadBadgets();
  }

  protected get isShortcutActive(): boolean {
    return this.currentOption() === FilterTypeEnum.Shortcut;
  }

  protected get isCalendarActive(): boolean {
    return this.currentOption() === FilterTypeEnum.Calendar;
  }

  protected get isExtraActive(): boolean {
    return this.currentOption() === FilterTypeEnum.Extra;
  }

  protected compareSubjects = (subject: any, option: any) => {
    if (!!option.ids) {
      return isEqual(subject.ids, HttpParamsUtils.deserializeArray(option.ids));
    }
    return isEqual(subject.ids, flatten([option]));
  };

  public ngAfterViewInit() {
    this.loadListeners();
  }

  public onSetActive(value: number) {
    this.currentOption.set(value);
  }

  public showField(ctrl: FilterControlEnum, filters: FilterForm): boolean {
    if (this.isExtraActive) {
      return filters.filters.includes(ctrl);
    }
    return filters.fixed.includes(ctrl);
  }

  public onShortcut(value: any) {
    this.form.patchValue(value);
  }

  public onFilter() {
    const generic = this.decodeStatus();
    const { generic_status, ...form } = this.form.value;
    this._filterService.sidenav(({ worker_id, sent }) => ({ ...form, ...generic, worker_id, sent }));
  }

  public isEqual(value: any, filter: any) {
    if (!filter?.date_min || !filter?.date_max) {
      return false;
    }
    return isSameDay(value.date_min, filter.date_min) && isSameDay(value.date_max, filter.date_max);
  }

  private loadData() {
    this.level = this._dataService.level;
    this.grade = this._dataService.grade;
    this.group = this._dataService.group;
    this.subject = this._dataService.subject;
    this.teacher = this._dataService.teacher;
    this.userGroups = this._dataService.userGroups;
    this.preventInvalid();
  }

  private preventInvalid() {
    this.preventInvalidField(this.grade.data, FilterControlEnum.Grade, (res, current) => res.id === current);
    this.preventInvalidField(this.group.data, FilterControlEnum.Group, (res, current) => res.id === current);
    this.preventInvalidField(
      this.subject.data,
      FilterControlEnum.Subject,
      (res, current) => this.compareSubjects(res, { ids: current }),
      'default_subject_id',
    );
    this.preventInvalidField(this.teacher.data, FilterControlEnum.Author, (res, current) => res.user_id === current);
    this.preventInvalidField(this.userGroups.data, FilterControlEnum.UserGroup, (res, current) => res.id === current);
  }

  private preventInvalidField(data: Signal<any[]>, field: FilterControlEnum, compareFn: any, ...reset: string[]) {
    effect(() => {
      const res = data();
      const current = this.form.value[field];
      if (current) {
        const safe = res.some((r) => compareFn(r, current));
        if (!safe) {
          this.form.patchValue({ [field]: null, ...reset.reduce((p, c) => ({ ...p, [c]: null }), {}) });
        }
      }
    });
  }

  private loadBadgets(): Signal<any[]> {
    const form = toSignal(this.form.valueChanges, { initialValue: this.form.value });
    const store = toSignal(this._store.select(AppSelectors.appFeature), { initialValue: null });
    return computed(() =>
      [
        this.dateBadget(form()),
        this.levelBadgets(form(), this.level.data()),
        this.gradeBadgets(form(), this.grade.data()),
        this.groupBadgets(form(), this.group.data()),
        this.subjectBadgets(form(), this.subject.data()),
        this.userGroupsBadgets(form(), this.userGroups.data()),
        this.teacherBadgets(form(), this.teacher.data()),
        this.genericBadgets(form(), store(), this.config()),
      ].filter((badget) => !!badget),
    );
  }

  private dateBadget(form: any): any {
    const min = form?.date_min;
    const max = form?.date_max;
    if (form.date_min) {
      let label = null;
      if (isSameDay(min, max)) {
        const today = this._translateService.instant('geral.hoje');
        const date = format(min, 'dd/MM/yyyy');
        label = isToday(min) ? `${today} - ${date}` : date;
      } else {
        const until = this._translateService.instant('geral.ate');
        const minFormat = format(min, 'dd/MM/yyyy');
        const maxFormat = format(max, 'dd/MM/yyyy');
        label = `${minFormat} ${until} ${maxFormat}`;
      }

      return {
        label,
        active: () => {
          const isShorcut = SHORTCUTS.some(({ value }) => this.isEqual(value, this.form.value));
          return this.onSetActive(isShorcut ? FilterTypeEnum.Shortcut : FilterTypeEnum.Calendar);
        },
      };
    }
    return null;
  }

  private levelBadgets(form: any, data: any[] = []): any {
    if (!isEmpty(data) && !!form[FilterControlEnum.Level]) {
      return {
        label: data.find(({ id }) => id === form[FilterControlEnum.Level])?.descricao,
        active: () => this.onSetActive(FilterTypeEnum.Extra),
        clear: () => this.form.get(FilterControlEnum.Level).reset(),
      };
    }
    return null;
  }

  private gradeBadgets(form: any, data: any[] = []): any {
    if (!isEmpty(data) && !!form[FilterControlEnum.Grade]) {
      return {
        label: data.find(({ id }) => id === form[FilterControlEnum.Grade])?.name,
        active: () => this.onSetActive(FilterTypeEnum.Extra),
        clear: () => this.form.get(FilterControlEnum.Grade).reset(),
      };
    }
    return null;
  }

  private groupBadgets(form: any, data: any[] = []): any {
    if (!isEmpty(data) && !!form[FilterControlEnum.Group]) {
      return {
        label: data.find(({ id }) => id === form[FilterControlEnum.Group])?.name,
        active: () => this.onSetActive(FilterTypeEnum.Extra),
        clear: () => this.form.get(FilterControlEnum.Group).reset(),
      };
    }
    return null;
  }

  private subjectBadgets(form: any, data: any[] = []): any {
    if (!isEmpty(data) && !!form[FilterControlEnum.Subject]) {
      return {
        label: data.find(({ ids }) => this.compareSubjects({ ids }, { ids: form[FilterControlEnum.Subject] }))?.name,
        active: () => this.onSetActive(FilterTypeEnum.Extra),
        clear: () => {
          this.form.get(FilterControlEnum.Subject).reset();
          this.form.get('default_subject_id').reset();
        },
      };
    }
    return null;
  }

  private userGroupsBadgets(form: any, data: any[] = []): any {
    if (!isEmpty(data) && !!form[FilterControlEnum.UserGroup]) {
      return {
        label: data.find(({ id }) => id === form[FilterControlEnum.UserGroup])?.descricao,
        active: () => this.onSetActive(FilterTypeEnum.Extra),
        clear: () => this.form.get(FilterControlEnum.UserGroup).reset(),
      };
    }
    return null;
  }

  private teacherBadgets(form: any, data: any[] = []): any {
    if (!isEmpty(data) && !!form[FilterControlEnum.Author]) {
      return {
        label: data.find(({ user_id }) => user_id === form[FilterControlEnum.Author])?.nome,
        active: () => this.onSetActive(FilterTypeEnum.Extra),
        clear: () => this.form.get(FilterControlEnum.Author).reset(),
      };
    }
    return null;
  }

  private genericBadgets(form: any, store: any, config: any): any {
    if (config && !!form[FilterControlEnum.Generic]) {
      const status = config(store)?.status;
      if (!isEmpty(status)) {
        const item = status.find(({ key }) => key === form[FilterControlEnum.Generic]);
        if (!isNil(item.value)) {
          return {
            label: this._translateService.instant(item.description),
            active: () => this.onSetActive(FilterTypeEnum.Extra),
            clear: () => this.form.get(FilterControlEnum.Generic).reset(head<any>(status)?.key),
          };
        }
        return {
          label: this._translateService.instant(item.description),
          active: () => this.onSetActive(FilterTypeEnum.Extra),
        };
      }
    }
    return null;
  }

  private filterListeners() {
    const filter$ = this._filterService.filter$.pipe(takeUntilDestroyed(this._destroy), distinctUntilChanged(isEqual));
    const store$ = this._store.select(AppSelectors.appFeature).pipe(
      takeUntilDestroyed(this._destroy),
      notNull(),
      distinctUntilChanged(isEqual),
      tap(() =>
        this.form.reset({
          date_min: this.form.value.date_min,
          date_max: this.form.value.date_max,
        }),
      ),
    );
    this.config$ = combineLatest([filter$, store$]).pipe(
      takeUntilDestroyed(this._destroy),
      map(([filter, store]) => ({
        filter,
        filters: this.buildForm(store),
        status: this.buildStatus(store),
        period: this.validPeriod(store),
      })),
      tap(({ filter, status, period }) => {
        const deserialize = HttpParamsUtils.deserializeParams(filter);
        const date_min = this.safeMin(filter, period);
        const date_max = this.safeMax(filter, period);
        this.form.patchValue({
          ...deserialize,
          date_min: min([date_min, date_max]),
          date_max: max([date_min, date_max]),
          [FilterControlEnum.Generic]: this.safeStatus(filter, status),
        });
      }),
    );
    this.validatorsListeners();
  }

  private validPeriod(store: AppState) {
    if (store.usuario_ativo) {
      const bimestres = store.usuario_ativo.bimestres?.filter(
        (bimestre) => bimestre.ano === store.usuario_ativo.ano_letivo,
      );
      if (!isEmpty(bimestres)) {
        const start = startOfMonth(safeDate(first<any>(sortBy(bimestres, 'data_inicio')).data_inicio));
        const end = endOfMonth(safeDate(last<any>(sortBy(bimestres, 'data_fim')).data_fim));
        return { start, end };
      }
      return { start: startOfYear(subYears(new Date(), 1)), end: endOfYear(new Date()) };
    }
    return null;
  }

  private safeStatus(filter: any, status: StatusFilter[]): string {
    const safe = status
      .filter((s) => !isNil(s.value))
      .find((s) => {
        const key = Object.keys(filter).includes(s.type);
        const value = s.value === HttpParamsUtils.deserialize(filter[s.type]);
        return key && value;
      });
    return safe?.key || head(status)?.key;
  }

  private buildForm(store: AppState): FilterForm {
    const config = this.config();
    return config(store).filters;
  }

  private buildStatus(store: AppState): StatusFilter[] {
    const config = this.config();
    return config(store).status;
  }

  private initForm() {
    const ctrls = Object.fromEntries(Object.values(FilterControlEnum).map((key) => [key, null]));
    this.form = this._formBuilder.group({
      ...ctrls,
      default_subject_id: null,
      date_min: null,
      date_max: null,
    });
  }

  private decodeStatus() {
    const form = this.form.value[FilterControlEnum.Generic];
    if (form) {
      const [key, value] = form.split('#');
      return { [key]: value };
    }
    return {};
  }

  private validatorsListeners() {
    this.minValidators();
    this.maxValidators();
  }

  private safeMin(filter: any, period: any): any {
    const safe = [filter.date_min, filter.date_max].filter((date) => !!date);
    if (!isEmpty(safe)) {
      const minDate = min(safe);
      if (isBefore(minDate, period.start)) {
        return new Date();
      }
      return minDate;
    }
    return this.form.value.date_min;
  }

  private safeMax(filter: any, period: any): any {
    const safe = [filter.date_min, filter.date_max].filter((date) => !!date);
    if (!isEmpty(safe)) {
      const maxDate = max(safe);
      if (isAfter(maxDate, period.end)) {
        return new Date();
      }
      return maxDate;
    }
    return this.form.value.date_max;
  }

  private loadListeners() {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this._destroy), startWith(this.form.value), debounceDelay())
      .subscribe(() => this.onFilter());
  }

  private maxValidators() {
    const maxCtrl = this.form.get('date_max');
    const minCtrl = this.form.get('date_min');
    maxCtrl.valueChanges.pipe(takeUntilDestroyed(this._destroy), startWith(maxCtrl.value)).subscribe((date_max) => {
      const date_min = minCtrl.value;
      const limit = subMonths(date_max, INTERVAL);
      if (date_min < limit) {
        minCtrl.setValue(limit);
      }
    });
  }

  private minValidators() {
    const minCtrl = this.form.get('date_min');
    const maxCtrl = this.form.get('date_max');
    minCtrl.valueChanges.pipe(takeUntilDestroyed(this._destroy)).subscribe((date_min) => {
      const date_max = maxCtrl.value;
      const limit = addMonths(date_min, INTERVAL);
      if (date_max > limit) {
        maxCtrl.setValue(limit);
      }
    });
  }
}
